import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { AppError } from "@/jenesys-types/errors";

// interfaces
export interface BulkPublishingState {
  isBulkPublishFetchLoading: boolean;
  bulkPublishJobs: any[];
  bulkPublishFetchError?: AppError | undefined;
  isValidating: boolean;
  validationCompleted: boolean;
  validationError?: AppError | undefined;
  isBulkPublishLoading: boolean;
  bulkPublishError: AppError | undefined;
  bulkPublishSuccess: boolean;
  isBulkDeleteLoading: boolean;
  bulkDeleteError?: AppError | undefined;
  bulkDeleteSuccess: boolean;
  isManualPublishing: boolean;
  manualPublishError?: AppError | undefined;
}

interface FetchBulkPublishingPayload {
  clientId: string;
}

interface ValidateBulkPublishingPayload {
  clientId: string;
  documentsIds: string[];
}

interface BulkPublishingPayload {
  clientId: string;
  documentsIds: string[];
  bulkPublishBatchName: string;
}

interface BulkDeletePayload {
  clientId: string;
  documentsIds: string[];
  reason: string;
}

interface ManualPublishPayload {
  clientId: string;
  documents_ids: string[];
}

// types
export type FetchBulkPublishingAction =
  PayloadAction<FetchBulkPublishingPayload>;

export type ValidateBulkPublishingAction =
  PayloadAction<ValidateBulkPublishingPayload>;

export type BulkPublishingAction = PayloadAction<BulkPublishingPayload>;

export type BulkDeleteAction = PayloadAction<BulkDeletePayload>;

export type ManualPublishAction = PayloadAction<ManualPublishPayload>;

// initial state
export const initialState: BulkPublishingState = {
  isBulkPublishFetchLoading: false,
  bulkPublishJobs: [],
  bulkPublishFetchError: undefined,
  isValidating: false,
  validationCompleted: false,
  validationError: undefined,
  isBulkPublishLoading: false,
  bulkPublishError: undefined,
  bulkPublishSuccess: false,
  isBulkDeleteLoading: false,
  bulkDeleteError: undefined,
  bulkDeleteSuccess: false,
  isManualPublishing: false,
  manualPublishError: undefined
};

// slice
const name = "state/features/BulkPublishing";

const BulkPublishingSlice = createSlice({
  name,
  initialState,
  reducers: {
    fetchBulkPublishing: {
      reducer: (
        state: BulkPublishingState,
        _action: FetchBulkPublishingAction
      ) => {
        state.isBulkPublishFetchLoading = true;
        state.bulkPublishFetchError = undefined;
      },
      prepare: (clientId: string) => ({
        payload: { clientId }
      })
    },
    fetchBulkPublishingSuccess: {
      reducer: (state: BulkPublishingState, action: PayloadAction<any[]>) => {
        state.isBulkPublishFetchLoading = false;
        state.bulkPublishJobs = action.payload;
      },
      prepare: (bulkPublishJobs: any[]) => ({
        payload: bulkPublishJobs
      })
    },
    fetchBulkPublishingFailure: {
      reducer: (
        state: BulkPublishingState,
        action: PayloadAction<AppError>
      ) => {
        state.isBulkPublishFetchLoading = false;
        state.bulkPublishFetchError = {
          message: action.payload.message,
          code: action.payload.code
        };
      },
      prepare: (error: AppError) => ({
        payload: {
          message: error.message,
          code: error.code
        }
      })
    },
    validateBulkPublishing: {
      reducer: (state: BulkPublishingState) => {
        state.isValidating = true;
        state.validationCompleted = false;
        state.validationError = undefined;
      },
      prepare: (clientId: string, documentsIds: string[]) => ({
        payload: { clientId, documentsIds }
      })
    },
    validateBulkPublishingSuccess(state: BulkPublishingState) {
      state.isValidating = false;
      state.validationCompleted = true;
    },
    validateBulkPublishingFailure: {
      reducer: (
        state: BulkPublishingState,
        action: PayloadAction<AppError>
      ) => {
        state.isValidating = false;
        state.validationError = action.payload;
      },
      prepare: (error: AppError) => ({
        payload: error
      })
    },
    bulkPublishing: {
      reducer: (state: BulkPublishingState) => {
        state.isBulkPublishLoading = true;
        state.bulkPublishError = {} as AppError;
        state.bulkPublishSuccess = false;
      },
      prepare: (
        clientId: string,
        documentsIds: string[],
        bulkPublishBatchName: string
      ) => ({
        payload: { clientId, documentsIds, bulkPublishBatchName }
      })
    },
    bulkPublishingSuccess(state: BulkPublishingState) {
      state.isBulkPublishLoading = false;
      state.validationCompleted = false;
      state.bulkPublishSuccess = true;
    },
    bulkPublishingFailure: {
      reducer: (
        state: BulkPublishingState,
        action: PayloadAction<AppError>
      ) => {
        state.isBulkPublishLoading = false;
        state.bulkPublishError = action.payload;
        state.validationCompleted = false;
        state.bulkPublishSuccess = false;
      },
      prepare: (error: AppError) => ({
        payload: error
      })
    },
    bulkDelete: {
      reducer: (state: BulkPublishingState) => {
        state.isBulkDeleteLoading = true;
        state.bulkDeleteError = undefined;
        state.bulkDeleteSuccess = false;
      },
      prepare: (clientId: string, documentsIds: string[], reason: string) => ({
        payload: { clientId, documentsIds, reason }
      })
    },
    bulkDeleteSuccess(state: BulkPublishingState) {
      state.isBulkDeleteLoading = false;
      state.bulkDeleteSuccess = true;
    },
    bulkDeleteFailure: {
      reducer: (
        state: BulkPublishingState,
        action: PayloadAction<AppError>
      ) => {
        state.isBulkDeleteLoading = false;
        state.bulkDeleteError = action.payload;
      },
      prepare: (error: AppError) => ({
        payload: error
      })
    },
    manualPublish: {
      reducer: (state: BulkPublishingState) => {
        state.isManualPublishing = true;
        state.manualPublishError = undefined;
      },
      prepare: (clientId: string, documents_ids: string[]) => ({
        payload: { clientId, documents_ids }
      })
    },
    manualPublishSuccess(state: BulkPublishingState) {
      state.isManualPublishing = false;
    },
    manualPublishFailure: {
      reducer: (
        state: BulkPublishingState,
        action: PayloadAction<AppError>
      ) => {
        state.isManualPublishing = false;
        state.manualPublishError = action.payload;
      },
      prepare: (error: AppError) => ({
        payload: error
      })
    }
  }
});

// export actions
export const {
  fetchBulkPublishing,
  fetchBulkPublishingSuccess,
  fetchBulkPublishingFailure,
  validateBulkPublishing,
  validateBulkPublishingSuccess,
  validateBulkPublishingFailure,
  bulkPublishing,
  bulkPublishingSuccess,
  bulkPublishingFailure,
  bulkDelete,
  bulkDeleteSuccess,
  bulkDeleteFailure,
  manualPublish,
  manualPublishSuccess,
  manualPublishFailure
} = BulkPublishingSlice.actions;

// selectors

export const selectBulkPublishingState = (state: {
  features: { BulkPublishing: BulkPublishingState };
}): BulkPublishingState => state.features.BulkPublishing;

export const selectBulkPublishingJobs = createSelector(
  selectBulkPublishingState,
  (state) => state.bulkPublishJobs
);

export const selectIsBulkPublishFetchLoading = createSelector(
  selectBulkPublishingState,
  (state) => state.isBulkPublishFetchLoading
);

export const selectBulkPublishFetchError = createSelector(
  selectBulkPublishingState,
  (state) => state.bulkPublishFetchError
);

export const selectIsValidating = createSelector(
  selectBulkPublishingState,
  (state) => state.isValidating
);

export const selectIsValidationCompleted = createSelector(
  selectBulkPublishingState,
  (state) => state.validationCompleted
);

export const selectValidationError = createSelector(
  selectBulkPublishingState,
  (state) => state.validationError
);

export const selectIsBulkPublishLoading = createSelector(
  selectBulkPublishingState,
  (state) => state.isBulkPublishLoading
);

export const selectBulkPublishError = createSelector(
  selectBulkPublishingState,
  (state) => state.bulkPublishError
);

export const selectBulkPublishSuccess = createSelector(
  selectBulkPublishingState,
  (state) => state.bulkPublishSuccess
);

export const selectBulkDeleteSuccess = createSelector(
  selectBulkPublishingState,
  (state) => state.bulkDeleteSuccess
);

export const selectIsBulkDeleteLoading = createSelector(
  selectBulkPublishingState,
  (state) => state.isBulkDeleteLoading
);

export const selectBulkDeleteError = createSelector(
  selectBulkPublishingState,
  (state) => state.bulkDeleteError
);

export const selectIsManualPublishing = createSelector(
  selectBulkPublishingState,
  (state) => state.isManualPublishing
);

export const selectManualPublishError = createSelector(
  selectBulkPublishingState,
  (state) => state.manualPublishError
);

export default BulkPublishingSlice.reducer;
